import { Download } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import {
    AppBar,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogContent,
    Divider,
    FormControlLabel,
    Grid,
    MenuItem,
    Modal,
    Select,
    Switch,
    TextField,
    Toolbar,
    Typography,
} from '@mui/material';
import { Box, Container, Stack } from '@mui/system';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
    endOfMonth,
    endOfQuarter,
    endOfWeek,
    endOfYear,
    startOfMonth,
    startOfQuarter,
    startOfToday,
    startOfWeek,
    startOfYear,
    subMonths,
    subQuarters,
    subWeeks,
    subYears,
} from 'date-fns';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'sonner';
import { downloadStatementOfAccountReport } from '../../redux/Invoice/action';
import { saveFileFromResponse } from '../../utility/helpers/fileDownload';
import SaveAndSendSvg from '../../utility/Svg/SaveAndSendSvg';
import { toISOStringUTC, toISOStringUTCEnd } from '../../utility/helpers/commonFunction';

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    // p: 3,
};

const StatementAccountReportModal = ({ open, close, id, data }) => {
    console.log('🚀 ~ StatementAccountReportModal ~ data:', data);
    const primaryEmail = data?.email;
    const secondaryEmail = data?.secondaryEmail;
    const initialSelected = [...(primaryEmail ? [primaryEmail] : []), ...(secondaryEmail ? [secondaryEmail] : [])];

    const dispatch = useDispatch();
    const [isOutstanding, setIsOutstanding] = useState(false);
    const [selectedEmails, setSelectedEmails] = useState(initialSelected);
    const [emailBody, setEmailBody] = useState('');
    const [selectedTimePeriod, setSelectedTimePeriod] = useState('');
    const [fromDate, setFromDate] = React.useState(null);
    const [toDate, setToDate] = React.useState(null);
    const [showLoader, setShowLoader] = useState(false);

    const placeholderText = `Please find your statement of account attached for the period of ${
        fromDate ? dayjs(fromDate).format('DD MMMM YYYY') : `{Start Date}`
    } to ${toDate ? dayjs(toDate).format('DD MMMM YYYY') : `{End Date}`}. Please pay the outstanding invoices at your earliest convenience.`;

    const handleClose = () => {
        setIsOutstanding(false);
        close(false);
        setFromDate(null);
        setToDate(null);
        setSelectedTimePeriod('');
    };

    const handleEmailChange = (email) => {
        setSelectedEmails((prev) => (prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]));
    };

    const handleChangeTimePeriod = (event) => {
        const selectedType = event.target.value;
        setSelectedTimePeriod(selectedType);

        const today = startOfToday();
        let from = null;
        let to = null;

        switch (selectedType) {
            case 'today':
                from = dayjs(today);
                to = dayjs(today);
                break;
            case 'thisWeek':
                from = dayjs(startOfWeek(today));
                to = dayjs(endOfWeek(today));
                break;
            case 'thisMonth':
                from = dayjs(startOfMonth(today));
                to = dayjs(endOfMonth(today));
                break;
            case 'thisQuarter':
                from = dayjs(startOfQuarter(today));
                to = dayjs(endOfQuarter(today));
                break;
            case 'thisYear':
                from = dayjs(startOfYear(today));
                to = dayjs(endOfYear(today));
                break;
            case 'lastWeek':
                from = dayjs(startOfWeek(subWeeks(today, 1)));
                to = dayjs(endOfWeek(subWeeks(today, 1)));
                break;
            case 'lastMonth':
                from = dayjs(startOfMonth(subMonths(today, 1)));
                to = dayjs(endOfMonth(subMonths(today, 1)));
                break;
            case 'lastQuarter':
                from = dayjs(startOfQuarter(subQuarters(today, 1)));
                to = dayjs(endOfQuarter(subQuarters(today, 1)));
                break;
            case 'lastYear':
                from = dayjs(startOfYear(subYears(today, 1)));
                to = dayjs(endOfYear(subYears(today, 1)));
                break;
            case 'allTime':
                from = dayjs('01/01/2024');
                to = dayjs(today);
                break;
            case 'custom':
                // Allow manual input
                break;
            default:
                from = null;
                to = null;
                break;
        }

        setFromDate(from);
        setToDate(to);
    };
    const handleDownload = async (type) => {
        console.log('type', type, dayjs(fromDate).format('YYYY-MM-DD'), dayjs(toDate).format('YYYY-MM-DD'));
        if ((!fromDate && !toDate) || (dayjs(fromDate).format('YYYY-MM-DD') == 'Invalid Date' && dayjs(toDate).format('YYYY-MM-DD') == 'Invalid Date')) {
            return toast.error('Please enter from date and to date ');
        }
        if (!fromDate || dayjs(fromDate).format('YYYY-MM-DD') == 'Invalid Date') return toast.error('Please enter from date');
        if (!toDate || dayjs(toDate).format('YYYY-MM-DD') == 'Invalid Date') return toast.error('Please enter to date');

        if (fromDate && toDate) {
            setShowLoader(true);
            const payload = {
                isOutstanding,
                // fromDate: toISOStringUTC(fromDate),
                // toDate: toISOStringUTCEnd(toDate),
                fromDate: dayjs(fromDate).format('YYYY-MM-DD'),
                toDate: dayjs(toDate).format('YYYY-MM-DD'),
                format: type,
                customerId: id,
            };
            const resp = await dispatch(downloadStatementOfAccountReport(payload));
            setShowLoader(false);
            // on failure the response interceptor has already shown the server's message (no data, PDF failed, timeout)
            if (resp?.status == 200 && saveFileFromResponse(resp, `statement-of-account.${type}`)) {
                toast.success('Statement of account exported successfully');
            }
        }
    };
    const handleSave = async (type) => {
        if ((!fromDate && !toDate) || (dayjs(fromDate).format('YYYY-MM-DD') == 'Invalid Date' && dayjs(toDate).format('YYYY-MM-DD') == 'Invalid Date')) {
            return toast.error('Please enter from date and to date ');
        }
        if (!fromDate || dayjs(fromDate).format('YYYY-MM-DD') == 'Invalid Date') return toast.error('Please enter from date');
        if (!toDate || dayjs(toDate).format('YYYY-MM-DD') == 'Invalid Date') return toast.error('Please enter to date');

        if (fromDate && toDate) {
            setShowLoader(true);
            const payload = {
                isOutstanding,
                fromDate: toISOStringUTC(fromDate),
                toDate: toISOStringUTCEnd(toDate),
                sendEmail: true,
                emailTo: selectedEmails,
                emailBody: emailBody.trim() || placeholderText,
                format: type,
                customerId: id,
            };
            const resp = await dispatch(downloadStatementOfAccountReport(payload));
            setShowLoader(false);
            // on failure the response interceptor has already shown the server's message
            if (resp?.status == 200) {
                toast.success('Statement of account was successfully emailed');
            }
        }
    };
    const disablePrevDates = (toDate) => {
        const startSeconds = Date.parse(toDate);
        return (date) => {
            return Date.parse(date) > startSeconds;
        };
    };
    const disableFutureDates = (toDate) => {
        const startSeconds = Date.parse(toDate);
        return (date) => {
            return Date.parse(date) < startSeconds;
        };
    };
    return (
        <Box>
            <Modal open={open} onClose={handleClose} aria-labelledby="parent-modal-title" aria-describedby="parent-modal-description">
                {/* <form onSubmit={handleSubmit(onSubmit)}> */}
                <Box
                    sx={{
                        ...style,
                        border: 'none',
                        borderRadius: '8px 8px 0px 0px',
                    }}
                    className="modalBodyBoxContainer"
                >
                    <AppBar
                        color="secondary"
                        variant="elevation"
                        sx={{
                            height: '64px',
                            borderRadius: '8px 8px 0px 0px !important',
                            paddingRight: '0px !important',
                        }}
                    >
                        <Toolbar variant="regular">
                            <Typography variant="h6" width={'98%'} color={'white'}>
                                Share Statement of Account
                            </Typography>
                            <Stack direction={'row'}>
                                <div onClick={handleClose} style={{ cursor: 'pointer', height: '32px' }}>
                                    <CloseIcon sx={{ fill: 'white', fontSize: 32 }} />
                                </div>
                            </Stack>
                        </Toolbar>
                    </AppBar>
                    <Container
                        // gap={2}
                        disableGutters
                        style={{
                            marginTop: '64px',
                        }}
                    >
                        <Grid
                            container
                            // spacing={3}
                            style={{
                                scrollbarColor: 'blue',
                                scrollbarWidth: 'thin',
                                overflowY: 'auto',
                                maxHeight: '78vh',
                            }}
                            padding={'24px 24px 0 24px'}
                        >
                            <Grid container rowGap={2} columnGap={3}>
                                <Grid item xs={12}>
                                    <Typography
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'flex-start',
                                            alignItems: 'center',
                                            gap: '4px',
                                            fontSize: '20px',
                                            fontWeight: 500,
                                        }}
                                    >
                                        <Download style={{ color: '#006CDD', width: '28px', height: '28px' }} />
                                        Download Report
                                    </Typography>
                                </Grid>
                                <Grid md={3.71}>
                                    <Stack>
                                        <div>
                                            <Typography
                                                variant="body1"
                                                style={{
                                                    fontWeight: 500,
                                                    fontSize: '16px',
                                                    lineHeight: '24px',
                                                    marginBottom: '8px',
                                                }}
                                                // padding={"5px"}
                                            >
                                                Select Time Period
                                            </Typography>
                                            <Select
                                                fullWidth
                                                MenuProps={{
                                                    PaperProps: {
                                                        style: {
                                                            maxHeight: 300, // Adjust this value as needed for your design
                                                        },
                                                    },
                                                }}
                                                value={selectedTimePeriod}
                                                onChange={(e) => handleChangeTimePeriod(e)}
                                            >
                                                <MenuItem key={'allTime'} value="allTime">
                                                    All Time
                                                </MenuItem>
                                                <MenuItem key={'custom'} value="custom">
                                                    Custom
                                                </MenuItem>
                                                <MenuItem key={'today'} value="today">
                                                    Today
                                                </MenuItem>{' '}
                                                <MenuItem key={'thisWeek'} value="thisWeek">
                                                    This Week
                                                </MenuItem>{' '}
                                                <MenuItem key={'thisMonth'} value="thisMonth">
                                                    This Month
                                                </MenuItem>{' '}
                                                <MenuItem key={'thisQuarter'} value="thisQuarter">
                                                    This Quarter
                                                </MenuItem>{' '}
                                                <MenuItem key={'thisYear'} value="thisYear">
                                                    This Year
                                                </MenuItem>{' '}
                                                <MenuItem key={'lastWeek'} value="lastWeek">
                                                    Last Week
                                                </MenuItem>
                                                <Divider style={{ width: '100%' }} />
                                                <MenuItem key={'lastMonth'} value="lastMonth">
                                                    Last Month
                                                </MenuItem>
                                                <MenuItem key={'lastQuarter'} value="lastQuarter">
                                                    Last Quarter
                                                </MenuItem>
                                                <MenuItem key={'lastYear'} value="lastYear">
                                                    Last Year
                                                </MenuItem>
                                            </Select>
                                        </div>
                                    </Stack>
                                </Grid>
                                <Grid md={3.71}>
                                    <Stack>
                                        <div>
                                            <Typography
                                                variant="body1"
                                                style={{
                                                    fontWeight: 500,
                                                    fontSize: '16px',
                                                    lineHeight: '24px',
                                                    marginBottom: '8px',
                                                }}
                                                // padding={"5px"}
                                            >
                                                From
                                            </Typography>
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DatePicker
                                                    format="DD/MM/YYYY"
                                                    value={fromDate}
                                                    onChange={(newValue) => {
                                                        setFromDate(dayjs(dayjs(newValue).format('YYYY-MM-DD')));
                                                        setSelectedTimePeriod('custom');
                                                    }}
                                                    renderInput={(params) => <TextField {...params} fullWidth />}
                                                    // disabled={selectedTimePeriod !== "custom"}
                                                    shouldDisableDate={disablePrevDates(toDate)}
                                                />
                                            </LocalizationProvider>
                                        </div>{' '}
                                    </Stack>
                                </Grid>
                                <Grid md={3.71}>
                                    <Stack>
                                        <div>
                                            <Typography
                                                variant="body1"
                                                style={{
                                                    fontWeight: 500,
                                                    fontSize: '16px',
                                                    lineHeight: '24px',
                                                    marginBottom: '8px',
                                                }}
                                                // padding={"5px"}
                                            >
                                                To
                                            </Typography>
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DatePicker
                                                    format="DD/MM/YYYY"
                                                    value={toDate}
                                                    onChange={(newValue) => {
                                                        setToDate(dayjs(dayjs(newValue).format('YYYY-MM-DD')));
                                                        setSelectedTimePeriod('custom');
                                                    }}
                                                    renderInput={(params) => <TextField {...params} fullWidth />}
                                                    // disabled={selectedTimePeriod !== "custom"}
                                                    shouldDisableDate={disableFutureDates(fromDate)}
                                                />
                                            </LocalizationProvider>
                                        </div>
                                    </Stack>
                                </Grid>

                                <Grid xs={12}>
                                    <Stack flexDirection={'row'} alignItems={'center'}>
                                        <Switch color="primary" size="medium" checked={isOutstanding} onChange={(e) => setIsOutstanding(e.target.checked)} />
                                        <Typography ml={1}>Show Outstanding Invoices Only</Typography>
                                    </Stack>
                                </Grid>

                                <Grid item xs={12}>
                                    <Stack spacing={2} direction="row" justifyContent={'flex-end'}>
                                        <Button variant="contained" color="primary" onClick={() => handleDownload('pdf')}>
                                            EXPORT PDF
                                        </Button>
                                        <Button variant="contained" color="primary" onClick={() => handleDownload('csv')}>
                                            EXPORT CSV
                                        </Button>
                                    </Stack>
                                </Grid>

                                <Grid item xs={12}>
                                    <Divider
                                        sx={{
                                            color: 'rgba(0, 0, 0, 0.10)',
                                            margin: '0 0',
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Stack direction={'row'} alignItems={'center'} justifyContent={'flex-start'} gap={'2px'}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
                                            <path
                                                d="M23.3333 4.66675C23.922 4.66656 24.489 4.88889 24.9206 5.28916C25.3523 5.68943 25.6167 6.23807 25.6608 6.82508L25.6667 7.00008V21.0001C25.6669 21.5888 25.4445 22.1557 25.0443 22.5874C24.644 23.019 24.0953 23.2834 23.5083 23.3276L23.3333 23.3334H4.66667C4.07799 23.3336 3.51101 23.1113 3.07936 22.711C2.64772 22.3107 2.38332 21.7621 2.33917 21.1751L2.33333 21.0001V19.8334H4.66667V21.0001H23.3333V8.64975L15.2367 16.7464C14.9304 17.0526 14.5215 17.2344 14.089 17.2566C13.6565 17.2788 13.2312 17.1398 12.8952 16.8666L12.7622 16.7464L4.66667 8.64975V9.33342H2.33333V7.00008C2.33315 6.41141 2.55547 5.84442 2.95575 5.41278C3.35602 4.98113 3.90465 4.71673 4.49167 4.67258L4.66667 4.66675H23.3333ZM7 15.1667C7.30942 15.1667 7.60617 15.2897 7.82496 15.5085C8.04375 15.7273 8.16667 16.024 8.16667 16.3334C8.16667 16.6428 8.04375 16.9396 7.82496 17.1584C7.60617 17.3772 7.30942 17.5001 7 17.5001H1.16667C0.857247 17.5001 0.560501 17.3772 0.341709 17.1584C0.122916 16.9396 0 16.6428 0 16.3334C0 16.024 0.122916 15.7273 0.341709 15.5085C0.560501 15.2897 0.857247 15.1667 1.16667 15.1667H7ZM21.6837 7.00008H6.31633L14 14.6837L21.6837 7.00008ZM5.83333 11.6667C6.13069 11.6671 6.41671 11.7809 6.63293 11.9851C6.84916 12.1892 6.97927 12.4682 6.9967 12.765C7.01413 13.0619 6.91755 13.3542 6.72669 13.5822C6.53584 13.8102 6.26511 13.9568 5.96983 13.9919L5.83333 14.0001H2.33333C2.03597 13.9998 1.74996 13.8859 1.53374 13.6818C1.31751 13.4776 1.18739 13.1986 1.16997 12.9018C1.15254 12.6049 1.24912 12.3126 1.43998 12.0846C1.63083 11.8566 1.90156 11.71 2.19683 11.6749L2.33333 11.6667H5.83333Z"
                                                fill="#006CDD"
                                            />
                                        </svg>
                                        <Typography fontSize={20} fontWeight={500} lineHeight={'120%'}>
                                            Email Report to Customer
                                        </Typography>
                                    </Stack>
                                </Grid>

                                <Grid item xs={12}>
                                    <Stack gap={'0'}>
                                        <Typography fontSize={16} fontWeight={500} lineHeight={'150%'}>
                                            {`Select Email(s)`}
                                        </Typography>
                                        {primaryEmail && (
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={selectedEmails.includes(primaryEmail)}
                                                        onChange={() => handleEmailChange(primaryEmail)}
                                                    />
                                                }
                                                label={`Primary (${primaryEmail})`}
                                            />
                                        )}
                                        {secondaryEmail && (
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={selectedEmails.includes(secondaryEmail)}
                                                        onChange={() => handleEmailChange(secondaryEmail)}
                                                    />
                                                }
                                                label={`Secondary (${secondaryEmail})`}
                                            />
                                        )}
                                    </Stack>
                                </Grid>
                                <Grid item xs={12}>
                                    <Stack gap={'8px'}>
                                        <Typography fontSize={16} fontWeight={500} lineHeight={'150%'}>
                                            {`Add a message for ${data?.company ? data?.company : `${data?.firstName} ${data?.lastName}`}`}
                                        </Typography>

                                        <TextField
                                            multiline
                                            rows={2}
                                            value={emailBody}
                                            onChange={(e) => setEmailBody(e.target.value)}
                                            placeholder={placeholderText}
                                        />
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item container justifyContent={'flex-end'} p={3}>
                            <Stack spacing={2} direction="row">
                                <Button variant="contained" startIcon={<SaveAndSendSvg />} size="large" onClick={() => handleSave('pdf')}>
                                    email to customer
                                </Button>
                            </Stack>
                        </Grid>
                    </Container>
                </Box>
                {/* </form> */}
            </Modal>
            <Dialog open={showLoader}>
                <DialogContent>
                    <CircularProgress />
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default StatementAccountReportModal;
